import { morph } from './morph.js';
import { isMobileBreakpoint } from './utilities.js';

/**
 * After morph, reset only **filter** accordions (`.facets__item`). Do not touch sorting/menus —
 * they use `accordion-custom` without `.facets__item`; forcing `details.open = false` broke filters.
 *
 * @param {HTMLElement | null} sectionRoot
 */
function syncFacetAccordionDetailsOpenState(sectionRoot) {
  if (!sectionRoot) return;
  const isMobile = isMobileBreakpoint();
  for (const host of sectionRoot.querySelectorAll('accordion-custom.facets__item')) {
    if (!(host instanceof HTMLElement)) continue;
    const details = host.querySelector('details');
    if (!(details instanceof HTMLDetailsElement)) continue;
    const shouldOpen =
      (isMobile && host.hasAttribute('open-by-default-on-mobile')) ||
      (!isMobile && host.hasAttribute('open-by-default-on-desktop'));
    details.open = shouldOpen;
  }
}

/**
 * A class to re-render sections using the Section Rendering API
 */
class SectionRenderer {
  /**
   * The cache of section HTML
   * @type {Map<string, string>}
   */
  #cache = new Map();

  /**
   * The abort controllers by section ID
   * @type {Map<string, AbortController>}
   */
  #abortControllersBySectionId = new Map();

  /**
   * The pending promises
   * @type {Map<string, Promise<string>>}
   */
  #pendingPromises = new Map();

  constructor() {
    window.addEventListener('load', this.#cachePageSections.bind(this));
  }

  /**
   * Renders a section
   * @param {string} sectionId - The section ID
   * @param {Object} [options] - The options
   * @param {boolean} [options.cache] - Whether to use the cache
   * @returns {Promise<string>} The rendered section HTML
   */
  async renderSection(sectionId, options) {
    const { cache = !Shopify.designMode } = options ?? {};
  
    this.#abortPendingMorph(sectionId);
  
    const abortController = new AbortController();
    this.#abortControllersBySectionId.set(sectionId, abortController);
  
    const sectionHTML = await this.getSectionHTML(sectionId, cache);
  
    if (!abortController.signal.aborted) {
      this.#abortControllersBySectionId.delete(sectionId);
  
      try {
        await morphSection(sectionId, sectionHTML);
      } catch (error) {
        console.error(`[SectionRenderer] Error morphing section ${sectionId}:`, error);
      }
  
      // Wait for DOM to stabilize
      await new Promise(resolve =>
        requestAnimationFrame(() =>
          requestAnimationFrame(resolve)
        )
      );

      syncFacetAccordionDetailsOpenState(
        document.getElementById(buildSectionSelector(normalizeSectionId(sectionId)))
      );

      /* --------------------------------------------------
         FILTER → PILL SYNC LOGIC (optimized + correct)
      -------------------------------------------------- */
  
      const normalizeText = (text = '') =>
        text.trim().toLowerCase().replace(/\s*\([^)]*\)\s*/g, '');
  
      /* -------------------------
         1. Build pill lookup map
      ------------------------- */
      const pillMap = new Map();
  
      document.querySelectorAll('quick-filter-pill').forEach(pill => {
        const textEl = pill.querySelector('.quickFilter__text');
        if (!textEl) return;
  
        const key = normalizeText(textEl.textContent);
        pillMap.set(key, pill);
      });
  
      /* -------------------------
         2. Sync checkbox → pill
      ------------------------- */
      const facetLabels = document.querySelectorAll(
        '.facets--vertical .checkbox__label-text'
      );
  
      facetLabels.forEach(labelEl => {
        const mainFilter = normalizeText(labelEl.textContent);
  
        const item = labelEl.closest('.facets__inputs-list-item');
        const input = item?.querySelector('input[type="checkbox"]');
  
        if (!input) return;
  
        const pill = pillMap.get(mainFilter);
        if (!pill) return;
  
        // active state
        pill.classList.toggle('active', input.checked);
  
        // disabled state
        pill.classList.toggle('disabled', input.disabled);
  
        // if pill contains real button/input (best practice)
        const control = pill.querySelector('button, input');
        if (control) {
          control.disabled = input.disabled;
        }
      });
  
      /* --------------------------------------------------
         Wishlist re-init
      -------------------------------------------------- */
  
      if (typeof ReloadSmartWishlist === 'function') {
        try {
          ReloadSmartWishlist();
        } catch (error) {
          console.error('[SectionRenderer] Error reloading wishlist:', error);
        }
      }

      if (typeof window.initMainCollectionStickyFilters === 'function') {
        try {
          window.initMainCollectionStickyFilters();
        } catch (error) {
          console.error('[SectionRenderer] Error re-initializing collection sticky filters:', error);
        }
      }
    }
  
    return sectionHTML;
  }
  
  /**
   * Aborts an existing morph for a section
   * @param {string} sectionId - The section ID
   */
  #abortPendingMorph(sectionId) {
    const existingAbortController = this.#abortControllersBySectionId.get(sectionId);
    if (existingAbortController) {
      existingAbortController.abort();
    }
  }

  /**
   * Gets the HTML for a section
   * @param {string} sectionId - The section ID
   * @param {boolean} useCache - Whether to use the cache
   * @param {URL} url - The URL to render the section for
   * @returns {Promise<string>} The rendered section HTML
   */
  async getSectionHTML(sectionId, useCache = true, url = new URL(window.location.href)) {
    const sectionUrl = buildSectionRenderingURL(sectionId, url);

    let pendingPromise = this.#pendingPromises.get(sectionUrl);
    if (pendingPromise) return pendingPromise;

    if (useCache) {
      const cachedHTML = this.#cache.get(sectionUrl);

      if (cachedHTML) return cachedHTML;
    }

    pendingPromise = fetch(sectionUrl).then((response) => {
      return response.text();
    });

    this.#pendingPromises.set(sectionUrl, pendingPromise);

    const sectionHTML = await pendingPromise;
    this.#pendingPromises.delete(sectionUrl);

    this.#cache.set(sectionUrl, sectionHTML);
    return sectionHTML;
  }

  /**
   * Caches the page sections
   */
  #cachePageSections() {
    for (const section of document.querySelectorAll('.shopify-section')) {
      const url = buildSectionRenderingURL(section.id);
      if (this.#cache.get(url)) return;
      if (containsShadowRoot(section)) return;

      this.#cache.set(url, section.outerHTML);
    }
  }
}

const SECTION_ID_PREFIX = 'shopify-section-';

/**
 * Builds a section rendering URL
 * @param {string} sectionId - The section ID
 * @param {URL} url - The URL to render the section for
 * @returns {string} The section rendering URL
 */
function buildSectionRenderingURL(sectionId, url = new URL(window.location.href)) {
  url.searchParams.set('section_id', normalizeSectionId(sectionId));
  url.searchParams.sort();

  return url.toString();
}

/**
 * Builds a section selector
 * @param {string} sectionId - The section ID
 * @returns {string} The section selector
 */
export function buildSectionSelector(sectionId) {
  return `${SECTION_ID_PREFIX}${sectionId}`;
}

/**
 * Normalizes a section ID
 * @param {string} sectionId - The section ID
 * @returns {string} The normalized section ID
 */
export function normalizeSectionId(sectionId) {
  return sectionId.replace(new RegExp(`^${SECTION_ID_PREFIX}`), '');
}

/**
 * Checks if an element contains a shadow root
 * @param {Element} element - The element to check
 * @returns {boolean} Whether the element contains a shadow root
 */
function containsShadowRoot(element) {
  return !!element.shadowRoot || Array.from(element.children).some(containsShadowRoot);
}

/**
 * @typedef {(previousElement: HTMLElement, newElement: HTMLElement) => void} UpdateCallback
 */

/**
 * Morphs the existing section element with the new section contents
 *
 * @param {string} sectionId - The section ID
 * @param {string} html - The new markup the section should morph into
 */
export async function morphSection(sectionId, html) {
  const fragment = new DOMParser().parseFromString(html, 'text/html');
  const existingElement = document.getElementById(buildSectionSelector(sectionId));
  const newElement = fragment.getElementById(buildSectionSelector(sectionId));

  if (!existingElement) {
    throw new Error(`Section ${sectionId} not found`);
  }

  if (!newElement) {
    throw new Error(`Section ${sectionId} not found in the section rendering response`);
  }

  morph(existingElement, newElement);
}

export const sectionRenderer = new SectionRenderer();